window.ttx = window.ttx || {};
ttx.workflow = ttx.workflow || {};

ttx.workflow._pending = ttx.workflow._pending || {};

function buildFallbackWorkflow(action, vm, extra) {
    const params = vm.getParams?.() || {};
    const value = vm.getData?.() || vm._get_data?.('value') || {};
    const actionLabel = extra?.actionLabel || (action === 'submit' ? '提交' : action === 'reject' ? '驳回' : '审批');
    const title = extra?.title || vm._get_data?.('title') || params.title || '审批流';
    const billNo = value.billno || value.billNo || params.billno || params.billNo || params.params?.id || params.id || '-';
    const status = value.billstatus || value.state || value.status || value.approvalStatus || '待处理';

    return {
        action,
        actionLabel,
        title: extra?.panelTitle || '审批流',
        subtitle: extra?.subtitle || (action === 'submit' ? '提交前确认流程节点。' : '处理前确认流程节点。'),
        summary: {
            title,
            billNo,
            status,
        },
        steps: extra?.steps || [
            { key: 'start', title: '发起', meta: '当前单据准备进入流程', active: action === 'submit' },
            { key: 'review', title: action === 'reject' ? '驳回处理' : '审批处理', meta: '待处理人执行操作', active: action !== 'submit' },
            { key: 'finish', title: '完成', meta: '流程结束', active: false },
        ],
        requireRemark: action === 'reject',
        // 不带 remark：面板首次打开时按单据/动作清空，流程刷新时保留用户已填写的意见
    };
}

function getPaneIndex(vm) {
    return vm?.getCache?.('paneIndex');
}

/**
 * 解析审批流目标单据：extra 优先（列表行场景），否则取 VM 自身数据。
 * 列表 VM 的 value 是数组，只能靠 extra.billId 指定当前行。
 */
function resolveBill(vm, extra) {
    const params = vm?.getParams?.() || {};
    const value = vm?.getData?.() || vm?._get_data?.('value') || {};
    const billno = extra?.billno || params.billno || params.billNo
        || (Array.isArray(value) ? '' : (value.billno || value.billNo))
        || vm?._get_data?.('serviceCode') || '';
    const id = extra?.billId || params.params?.id || params.id
        || (Array.isArray(value) ? '' : value.id) || '';
    return { billno, id };
}

function toggleWorkflowPanel(paneIndex, workflow) {
    ttx.portalactions.togglePanelWorkflow(paneIndex, {
        visible: true,
        action: workflow.action,
        actionLabel: workflow.actionLabel,
        title: workflow.title,
        subtitle: workflow.subtitle,
        summary: workflow.summary,
        steps: workflow.steps,
        requireRemark: workflow.requireRemark,
        allowApprove: workflow.allowApprove,
        allowReject: workflow.allowReject,
        allowWithdraw: workflow.allowWithdraw,
        // 处理意见只在明确有值时下发：打开/刷新流程时不能下发空串，
        // 否则会把用户在面板里已经填写的意见清空，接着点「同意」就以空意见提交，
        // 服务端按环节「意见必填」校验并报「请填写审批意见」
        remark: workflow.remark,
        // 只读查看（列表行「审批流」按钮）：隐藏处理意见与操作按钮
        viewOnly: !!workflow.viewOnly,
        // 显式回传目标单据：列表行查看时面板按这两个字段拉流程，
        // 置 null 可清掉上一次遗留的行数据（卡片页仍由 VM 自身解析）
        billno: workflow.billno || null,
        billId: workflow.billId || null,
    });
}

ttx.workflow.fetch = function (vm, action, extra) {
    return new Promise(function (resolve) {
        const bill = resolveBill(vm, extra);
        const billno = bill.billno;
        const id = bill.id;
        if (!billno || !id || !ttx.ajax?.xhrUtil) {
            resolve({ ...buildFallbackWorkflow(action, vm, extra), billno: billno || null, billId: id || null });
            return;
        }

        ttx.ajax.xhrUtil({
            url: '/api/bill/workflow?billno=' + encodeURIComponent(billno) + '&id=' + encodeURIComponent(id) + '&action=' + encodeURIComponent(action),
            method: 'get',
            success: function (res) {
                const workflow = buildFallbackWorkflow(action, vm, extra);
                const data = res?.data || {};
                resolve({
                    ...workflow,
                    billno: billno,
                    billId: id,
                    actionLabel: data.actionLabel || workflow.actionLabel,
                    summary: { ...workflow.summary, ...(data.summary || {}) },
                    steps: data.steps || workflow.steps,
                    requireRemark: data.requireRemark != null ? data.requireRemark : workflow.requireRemark,
                    allowApprove: data.allowApprove !== false,
                    allowReject: data.allowReject !== false,
                    allowWithdraw: data.allowWithdraw !== false,
                });
            },
            fail: function () {
                resolve({ ...buildFallbackWorkflow(action, vm, extra), billno: billno, billId: id });
            }
        });
    });
};

ttx.workflow.open = function (action, vm, extra) {
    if (!vm) return Promise.resolve(false);
    const paneIndex = getPaneIndex(vm);
    if (paneIndex === undefined || paneIndex === null || !ttx.portalactions?.togglePanelWorkflow) return Promise.resolve(false);

    const bill = resolveBill(vm, extra);
    const fallback = { ...buildFallbackWorkflow(action, vm, extra), billno: bill.billno || null, billId: bill.id || null };
    toggleWorkflowPanel(paneIndex, fallback);

    return new Promise(function (resolve) {
        ttx.workflow._pending[paneIndex] = {
            vm,
            action,
            extra,
            resolve,
        };
        ttx.workflow.fetch(vm, action, extra).then(function (workflow) {
            toggleWorkflowPanel(paneIndex, workflow);
        });
    });
};

/**
 * 只读查看审批流（卡片/列表的「审批流」按钮）：
 * 打开侧边栏展示流程节点与流转记录，不注册待确认回调，因此不会触发任何审批动作。
 * extra: { billno, billId, title, paneIndex }，列表行场景用 billId 指定当前行。
 */
ttx.workflow.view = function (vm, extra) {
    if (!vm) return false;
    const paneIndex = extra?.paneIndex ?? getPaneIndex(vm);
    if (paneIndex === undefined || paneIndex === null || !ttx.portalactions?.togglePanelWorkflow) return false;

    const bill = resolveBill(vm, extra);
    if (!bill.billno || !bill.id) {
        ttx.toast?.warning?.('当前单据缺少单据信息，无法查看审批流');
        return false;
    }

    toggleWorkflowPanel(paneIndex, {
        action: 'approve',
        actionLabel: '查看',
        title: '审批流',
        subtitle: '查看当前单据的审批流程与流转记录。',
        summary: { title: extra?.title || '', billNo: bill.billno, status: '' },
        steps: [],
        viewOnly: true,
        billno: bill.billno,
        billId: bill.id,
    });
    return true;
};

ttx.workflow.confirm = function (paneIndex, payload) {
    const pending = ttx.workflow._pending[paneIndex];
    if (!pending) return;
    delete ttx.workflow._pending[paneIndex];
    pending.resolve({ confirmed: true, ...(payload || {}) });
};

ttx.workflow.cancel = function (paneIndex) {
    const pending = ttx.workflow._pending[paneIndex];
    if (!pending) return;
    delete ttx.workflow._pending[paneIndex];
    ttx.portalactions.togglePanelWorkflow(paneIndex, { visible: false });
    pending.resolve({ confirmed: false });
};
